import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { 
  ArrowLeft, FileText, Check, X, Calendar, 
  Phone, Mail, AlertTriangle, Send, Star,
  FileSignature, Eye, Download, Loader2, Shield, Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import StarRating from '../../components/StarRating';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'pending_review', label: 'Pending Review', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'approved', label: 'Approved', color: 'bg-blue-100 text-blue-800', ownerOnly: true },
  { value: 'declined', label: 'Declined', color: 'bg-red-100 text-red-800', ownerOnly: true },
  { value: 'phone_screen', label: 'Phone Screen Invited', color: 'bg-violet-100 text-violet-800' },
  { value: 'phone_scheduled', label: 'Phone Interview Scheduled', color: 'bg-purple-100 text-purple-800' },
  { value: 'phone_completed', label: 'Phone Interview Passed', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'in_person_invited', label: 'In-Person Invited', color: 'bg-sky-100 text-sky-800' },
  { value: 'in_person_scheduled', label: 'In-Person Scheduled', color: 'bg-cyan-100 text-cyan-800' },
  { value: 'interview_sent', label: 'Interview Sent', color: 'bg-purple-100 text-purple-800' },
  { value: 'interview_confirmed', label: 'Interview Confirmed', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'interviewed', label: 'Interviewed', color: 'bg-teal-100 text-teal-800' },
  { value: 'hired', label: 'Hired', color: 'bg-green-100 text-green-800', ownerOnly: true },
  { value: 'not_moving_forward', label: 'Not Moving Forward', color: 'bg-gray-100 text-gray-800', ownerOnly: true },
];

const CandidateDetailPage = () => {
  const { candidateId } = useParams();
  const navigate = useNavigate();
  const { isOwner } = useAuth();
  const [candidate, setCandidate] = useState(null);
  const [interviewSlots, setInterviewSlots] = useState([]);
  const [offerLetter, setOfferLetter] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('private');
  const [submitting, setSubmitting] = useState(false);
  const [resending, setResending] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  
  // Offer letter dialog state
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);
  const [offerPreview, setOfferPreview] = useState('');
  const [offerData, setOfferData] = useState({
    start_date: '',
    pay_rate: '',
    template_id: '',
    custom_notes: ''
  });
  const [previewLoading, setPreviewLoading] = useState(false);
  
  // Multi-template state
  const [offerTemplates, setOfferTemplates] = useState([]);
  const [offerStep, setOfferStep] = useState('select'); // 'select', 'details', 'preview'

  // Background check state
  const [bgCheck, setBgCheck] = useState(null);
  const [bgCheckLoading, setBgCheckLoading] = useState(false);

  // Activity log state (Part 5)
  const [activityLog, setActivityLog] = useState([]);
  const [loadingActivity, setLoadingActivity] = useState(false);

  // Phase interview action state
  const [advancingPhone, setAdvancingPhone] = useState(false);
  const [advancingInPerson, setAdvancingInPerson] = useState(false);
  const [completingPhone, setCompletingPhone] = useState(false);
  const [completingInPerson, setCompletingInPerson] = useState(false);

  useEffect(() => {
    fetchCandidate();
    fetchInterviewSlots();
    fetchOfferTemplates();
    fetchBackgroundCheck();
    fetchCandidateActivity();
  }, [candidateId]);

  useEffect(() => {
    if (candidate?.offer_letter_id) {
      fetchOfferLetter();
    }
  }, [candidate]);

  const fetchCandidate = async () => {
    try {
      const response = await axios.get(`${API}/candidates/${candidateId}`);
      setCandidate(response.data);
    } catch (error) {
      toast.error('Failed to load candidate');
      navigate('/dashboard/candidates');
    } finally {
      setLoading(false);
    }
  };

  const fetchInterviewSlots = async () => {
    try {
      const response = await axios.get(`${API}/interview-slots`, { params: { available_only: true } });
      setInterviewSlots(response.data);
    } catch (error) {
      console.error('Failed to load interview slots');
    }
  };

  const fetchOfferLetter = async () => {
    try {
      const response = await axios.get(`${API}/candidates/${candidateId}/offer-letter`);
      setOfferLetter(response.data);
    } catch (error) {
      console.error('Failed to load offer letter');
    }
  };

  const fetchOfferTemplates = async () => {
    try {
      const response = await axios.get(`${API}/offer-letter-templates`);
      setOfferTemplates(response.data);
    } catch (error) {
      console.error('Failed to load offer templates');
    }
  };

  const fetchBackgroundCheck = async () => {
    try {
      const response = await axios.get(`${API}/background-checks/${candidateId}`);
      setBgCheck(response.data);
    } catch (error) {
      console.error('Failed to load background check');
    }
  };

  const fetchCandidateActivity = async () => {
    setLoadingActivity(true);
    try {
      const response = await axios.get(`${API}/activity-log/candidate/${candidateId}`);
      setActivityLog(response.data);
    } catch (error) {
      console.error('Failed to load activity log');
    } finally {
      setLoadingActivity(false);
    }
  };
  // --- Phase Interview Actions ---
  const advanceToPhone = async () => {
    setAdvancingPhone(true);
    try {
      const res = await axios.post(`${API}/interview-scheduling/${candidateId}/advance-phone`);
      toast.success('Candidate advanced to phone interview! Scheduling link sent.');
      fetchCandidate();
      fetchCandidateActivity();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to advance candidate');
    } finally {
      setAdvancingPhone(false);
    }
  };

  const advanceToInPerson = async () => {
    setAdvancingInPerson(true);
    try {
      await axios.post(`${API}/interview-scheduling/${candidateId}/advance-in-person`);
      toast.success('Candidate advanced to in-person interview! Scheduling link sent.');
      fetchCandidate();
      fetchCandidateActivity();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to advance candidate');
    } finally {
      setAdvancingInPerson(false);
    }
  };

  const completePhone = async () => {
    setCompletingPhone(true);
    try {
      await axios.post(`${API}/interview-scheduling/${candidateId}/complete-phone`);
      toast.success('Phone interview marked complete!');
      fetchCandidate();
      fetchCandidateActivity();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete phone interview');
    } finally {
      setCompletingPhone(false);
    }
  };

  const completeInPerson = async () => {
    setCompletingInPerson(true);
    try {
      await axios.post(`${API}/interview-scheduling/${candidateId}/complete-in-person`);
      toast.success('In-person interview marked complete!');
      fetchCandidate();
      fetchCandidateActivity();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to complete in-person interview');
    } finally {
      setCompletingInPerson(false);
    }
  };



  const initiateBackgroundCheck = async () => {
    setBgCheckLoading(true);
    try {
      await axios.post(`${API}/background-checks/initiate`, {
        candidate_id: candidateId,
        package: 'caregiver_basic'
      });
      toast.success('Background check initiated');
      fetchBackgroundCheck();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate background check');
    } finally {
      setBgCheckLoading(false);
    }
  };

  const updateBgCheckStatus = async (bgCheckId, status) => {
    try {
      await axios.put(`${API}/background-checks/${bgCheckId}/status`, { status });
      toast.success('Background check updated');
      fetchBackgroundCheck();
      fetchCandidate();
    } catch (error) {
      toast.error('Failed to update background check');
    }
  };

  const openOfferDialog = () => {
    setOfferStep('select');
    setOfferData({ start_date: '', pay_rate: '', template_id: '', custom_notes: '' });
    setOfferPreview('');
    setOfferDialogOpen(true);
  };

  const updateStatus = async (newStatus) => {
    setSubmitting(true);
    try {
      await axios.put(`${API}/candidates/${candidateId}/status`, { status: newStatus });
      toast.success(`Status updated to ${newStatus.replace(/_/g, ' ')}`);
      fetchCandidate();
      fetchCandidateActivity();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setSubmitting(false);
    }
  };

  const updateRating = async (rating) => {
    try {
      if (rating === 0) {
        await axios.delete(`${API}/candidates/${candidateId}/rating`);
        setCandidate(prev => ({ ...prev, owner_rating: null }));
      } else {
        await axios.put(`${API}/candidates/${candidateId}/rating`, { rating });
        setCandidate(prev => ({ ...prev, owner_rating: rating }));
      }
    } catch (error) {
      toast.error('Failed to update rating');
    }
  };

  const resendNotification = async () => {
    setResending(true);
    try {
      const res = await axios.post(`${API}/candidates/${candidateId}/resend-notification`);
      toast.success(res.data.message);
      fetchCandidate();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resend notification');
    } finally {
      setResending(false);
    }
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/candidates/${candidateId}/notes`, {
        content: newNote,
        note_type: noteType
      });
      toast.success('Note added');
      setNewNote('');
      fetchCandidate();
      fetchCandidateActivity();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setSubmitting(false);
    }
  };

  const updateOnboarding = async (key, value) => {
    try {
      await axios.put(`${API}/candidates/${candidateId}/onboarding`, { [key]: value });
      fetchCandidate();
    } catch (error) {
      toast.error('Failed to update onboarding');
    }
  };

  const scheduleInterview = async (slotId) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/candidates/${candidateId}/schedule-interview?slot_id=${slotId}`);
      toast.success('Interview scheduled!');
      fetchCandidate();
      fetchInterviewSlots();
      fetchCandidateActivity();
    } catch (error) {
      toast.error('Failed to schedule interview');
    } finally {
      setSubmitting(false);
    }
  };

  const previewOfferLetter = async () => {
    if (!offerData.start_date || !offerData.pay_rate) {
      toast.error('Please fill in start date and pay rate');
      return;
    }
    if (offerTemplates.length > 0 && !offerData.template_id) {
      toast.error('Please select a template');
      return;
    }
    setPreviewLoading(true);
    try {
      const response = await axios.post(`${API}/candidates/${candidateId}/offer-letter/preview`, offerData);
      setOfferPreview(response.data.preview);
      setOfferStep('preview');
    } catch (error) {
      toast.error('Failed to generate preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const sendOfferLetter = async () => {
    if (!offerData.start_date || !offerData.pay_rate) {
      toast.error('Please fill in start date and pay rate');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/candidates/${candidateId}/offer-letter/send`, offerData);
      toast.success('Offer letter sent!');
      setOfferDialogOpen(false);
      setOfferStep('select');
      fetchCandidate();
      fetchOfferLetter();
      fetchCandidateActivity();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send offer letter');
    } finally {
      setSubmitting(false);
    }
  };

  const getSelectedTemplateName = () => {
    if (!offerData.template_id) return 'No template selected';
    const template = offerTemplates.find(t => t.id === offerData.template_id);
    return template ? template.name : 'Unknown template';
  };

  const getScoreColor = (score) => {
    if (score >= 75) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getScoreBg = (score) => {
    if (score >= 75) return 'bg-green-100 border-green-300';
    if (score >= 50) return 'bg-yellow-100 border-yellow-300';
    return 'bg-red-100 border-red-300';
  };

  const getOfferStatusBadge = () => {
    if (!offerLetter || offerLetter.status === 'not_sent') {
      return null;
    }
    if (offerLetter.status === 'signed') {
      return <Badge className="bg-green-100 text-green-800">Offer Signed</Badge>;
    }
    return <Badge className="bg-yellow-100 text-yellow-800">Offer Sent</Badge>;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#4B2D6E] border-t-transparent"></div>
      </div>
    );
  }

  if (!candidate) {
    return (
      <div className="p-6 text-center">
        <p className="text-[#666666]">Candidate not found</p>
      </div>
    );
  }

  const parsed = candidate.parsed_resume || {};
  const answers = candidate.answers || {};
  const contactInfo = candidate.contact_info || {};
  const candidateName = contactInfo.first_name 
    ? `${contactInfo.first_name} ${contactInfo.last_name}` 
    : (parsed.name && parsed.name !== 'N/A' ? parsed.name : null);
  const canSendOffer = ['hired', 'interviewed', 'interview_confirmed'].includes(candidate.status);

  return (
    <div className="p-6 space-y-6" data-testid="candidate-detail-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="flex items-start gap-4">
          <Button variant="ghost" onClick={() => navigate('/dashboard/candidates')} className="mt-1">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-[#4B2D6E] font-[Lato]">
              {candidateName || 'Unknown Candidate'}
            </h1>
            <p className="text-[#666666] mt-1">Applied for {candidate.job_title}</p>
            <div className="flex flex-wrap items-center gap-3 mt-2">
              <Badge className={STATUS_OPTIONS.find(s => s.value === candidate.status)?.color || 'bg-gray-100'}>
                {candidate.status?.replace(/_/g, ' ')}
              </Badge>
              {candidate.referrer && (
                <Badge variant="outline" className="border-[#4B2D6E] text-[#4B2D6E]">
                  Referred by {candidate.referrer.name}
                </Badge>
              )}
              {getOfferStatusBadge()}
              {candidate.mileage_flag && (
                <Badge className="bg-amber-100 text-amber-800 border border-amber-300" data-testid="mileage-flag-badge">
                  <AlertTriangle className="h-3.5 w-3.5 mr-1" />
                  {candidate.mileage_flag === 'concern' ? 'Mileage Policy Concern' : 'Mileage Policy Question'}
                </Badge>
              )}
            </div>
          </div>
        </div>
        
        {/* Quick Actions */}
        <div className="flex flex-wrap gap-2">
          {/* Phase 1: Approve / Decline */}
          {isOwner && candidate.status === 'pending_review' && (
            <>
              <Button
                onClick={() => setApproveDialogOpen(true)}
                disabled={submitting}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="approve-btn"
              >
                <Check className="mr-2 h-4 w-4" /> Approve
              </Button>
              <Button
                onClick={() => updateStatus('declined')}
                disabled={submitting}
                variant="outline"
                className="border-red-500 text-red-500 hover:bg-red-50"
                data-testid="decline-btn"
              >
                <X className="mr-2 h-4 w-4" /> Decline
              </Button>
            </>
          )}
          {isOwner && candidate.status === 'approved' && (
            <>
              <Button
                onClick={advanceToPhone}
                disabled={advancingPhone}
                className="bg-[#4B2D6E] hover:bg-[#3a2357] text-white"
                data-testid="advance-phone-btn"
              >
                {advancingPhone ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Phone className="mr-2 h-4 w-4" />}
                Advance to Phone Interview
              </Button>
              <Button
                onClick={() => updateStatus('pending_review')}
                disabled={submitting}
                variant="outline"
                className="border-yellow-500 text-yellow-600 hover:bg-yellow-50"
                data-testid="undo-approve-btn"
              >
                <ArrowLeft className="mr-2 h-4 w-4" /> Undo Approve
              </Button>
            </>
          )}
          {/* Phase 2: Phone Interview actions */}
          {isOwner && candidate.status === 'phone_scheduled' && (
            <Button
              onClick={completePhone}
              disabled={completingPhone}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="complete-phone-btn"
            >
              {completingPhone ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
              Pass Phone Interview
            </Button>
          )}
          {/* Phase 3: Advance to In-Person */}
          {isOwner && candidate.status === 'phone_completed' && (
            <Button
              onClick={advanceToInPerson}
              disabled={advancingInPerson}
              className="bg-[#E8722A] hover:bg-[#D6621F] text-white"
              data-testid="advance-in-person-btn"
            >
              {advancingInPerson ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Calendar className="mr-2 h-4 w-4" />}
              Advance to In-Person Interview
            </Button>
          )}
          {isOwner && candidate.status === 'in_person_scheduled' && (
            <Button
              onClick={completeInPerson}
              disabled={completingInPerson}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="complete-in-person-btn"
            >
              {completingInPerson ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />}
              Complete In-Person Interview
            </Button>
          )}
          {isOwner && candidate.status === 'interviewed' && (
            <Button
              onClick={() => updateStatus('hired')}
              disabled={submitting}
              className="bg-[#4CAF50] hover:bg-[#45a049] text-white"
              data-testid="hire-btn"
            >
              <Check className="mr-2 h-4 w-4" /> Mark as Hired
            </Button>
          )}
          {/* Not Moving Forward (available at any active interview phase) */}
          {isOwner && ['phone_screen', 'phone_scheduled', 'phone_completed', 'in_person_invited', 'in_person_scheduled'].includes(candidate.status) && (
            <Button
              onClick={() => updateStatus('not_moving_forward')}
              disabled={submitting}
              variant="outline"
              className="border-gray-400 text-gray-600 hover:bg-gray-50"
              data-testid="not-moving-forward-btn"
            >
              <X className="mr-2 h-4 w-4" /> Not Moving Forward
            </Button>
          )}
          {/* Resend Notification */}
          {isOwner && ['approved', 'interviewed', 'hired'].includes(candidate.status) && (
            <Button
              variant="outline"
              onClick={resendNotification}
              disabled={resending}
              className="border-[#4B2D6E] text-[#4B2D6E] hover:bg-[#4B2D6E]/5"
              data-testid="resend-notification-btn"
            >
              {resending ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Sending...</>
              ) : (
                <><Send className="mr-2 h-4 w-4" />Resend Notification</>
              )}
            </Button>
          )}
          {isOwner && canSendOffer && (!offerLetter || offerLetter.status === 'not_sent') && (
            <Dialog open={offerDialogOpen} onOpenChange={setOfferDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-[#E8722A] hover:bg-[#D6621F]" data-testid="send-offer-btn">
                  <FileSignature className="mr-2 h-4 w-4" /> Send Offer Letter
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Send Offer Letter to {candidateName || parsed.name}</DialogTitle>
                </DialogHeader>
                
                {/* Step 1: Select Template */}
                {offerStep === 'select' && (
                  <div className="space-y-4 mt-4">
                    {offerTemplates.length > 0 ? (
                      <>
                        <div className="space-y-2">
                          <Label className="text-base font-semibold">Select Offer Letter Template *</Label>
                          <p className="text-sm text-[#666666]">Choose the appropriate template for this position</p>
                        </div>
                        <div className="grid gap-3">
                          {offerTemplates.map((template) => (
                            <div
                              key={template.id}
                              onClick={() => setOfferData({ ...offerData, template_id: template.id })}
                              className={`p-4 border rounded-lg cursor-pointer transition-all ${
                                offerData.template_id === template.id
                                  ? 'border-[#E8722A] bg-[#E8722A]/5 ring-2 ring-[#E8722A]'
                                  : 'border-gray-200 hover:border-gray-300'
                              }`}
                              data-testid={`template-option-${template.id}`}
                            >
                              <div className="flex items-center justify-between">
                                <div>
                                  <p className="font-medium text-[#333333]">{template.name}</p>
                                  <p className="text-sm text-[#666666]">{template.position_type}</p>
                                </div>
                                {offerData.template_id === template.id && (
                                  <Check className="h-5 w-5 text-[#E8722A]" />
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                        <Button
                          onClick={() => setOfferStep('details')}
                          disabled={!offerData.template_id}
                          className="w-full bg-[#4B2D6E] hover:bg-[#3a2357]"
                        >
                          Continue with {getSelectedTemplateName()}
                        </Button>
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <FileSignature className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                        <p className="text-[#666666]">No templates configured yet.</p>
                        <p className="text-sm text-[#999999] mt-1">Go to Settings → Offer Letter to create templates.</p>
                        <Button
                          variant="outline"
                          onClick={() => setOfferStep('details')}
                          className="mt-4"
                        >
                          Continue with Default Template
                        </Button>
                      </div>
                    )}
                  </div>
                )}

                {/* Step 2: Enter Details */}
                {offerStep === 'details' && (
                  <div className="space-y-4 mt-4">
                    {offerData.template_id && (
                      <div className="p-3 bg-[#4B2D6E]/5 rounded-lg">
                        <p className="text-sm text-[#4B2D6E]">
                          <strong>Template:</strong> {getSelectedTemplateName()}
                        </p>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Start Date *</Label>
                        <Input
                          type="date"
                          value={offerData.start_date}
                          onChange={(e) => setOfferData({ ...offerData, start_date: e.target.value })}
                          data-testid="offer-start-date"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Hourly Pay Rate ($) *</Label>
                        <Input
                          type="text"
                          placeholder="e.g., 18.00"
                          value={offerData.pay_rate}
                          onChange={(e) => setOfferData({ ...offerData, pay_rate: e.target.value })}
                          data-testid="offer-pay-rate"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label>Custom Notes (optional)</Label>
                      <Textarea
                        placeholder="Any additional notes to include..."
                        value={offerData.custom_notes}
                        onChange={(e) => setOfferData({ ...offerData, custom_notes: e.target.value })}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setOfferStep('select')}
                        className="flex-1"
                      >
                        Back
                      </Button>
                      <Button
                        onClick={previewOfferLetter}
                        disabled={previewLoading || !offerData.start_date || !offerData.pay_rate}
                        className="flex-1 bg-[#4B2D6E] hover:bg-[#3a2357]"
                      >
                        {previewLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Eye className="mr-2 h-4 w-4" />}
                        Preview Letter
                      </Button>
                    </div>
                  </div>
                )}

                {/* Step 3: Preview and Confirm */}
                {offerStep === 'preview' && (
                  <div className="space-y-4 mt-4">
                    <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                      <p className="text-sm text-green-800 font-medium">
                        ✓ Preview generated successfully. Please review before sending.
                      </p>
                    </div>
                    <div className="border rounded-lg p-4 bg-[#FAFAFA] whitespace-pre-wrap text-sm max-h-[400px] overflow-y-auto font-mono">
                      {offerPreview}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setOfferStep('details')}
                        className="flex-1"
                      >
                        Back to Edit
                      </Button>
                      <Button
                        onClick={sendOfferLetter}
                        disabled={submitting}
                        className="flex-1 bg-[#E8722A] hover:bg-[#D6621F]"
                        data-testid="confirm-send-offer"
                      >
                        {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                        Confirm & Send
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Summary */}
        <div className="space-y-6">
          {/* Star Rating Card */}
          {isOwner && (
            <Card className="border-t-4 border-[#E8C547]" data-testid="star-rating-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-[#4B2D6E] flex items-center gap-2">
                  <Star className="h-5 w-5 text-[#E8C547]" />
                  Your Rating
                </CardTitle>
              </CardHeader>
              <CardContent>
                <StarRating 
                  rating={candidate.owner_rating || 0}
                  onChange={updateRating}
                  size="lg"
                  showLabel={false}
                />
                <p className="text-xs text-[#999999] mt-2">Your personal rating — not visible to candidates</p>
              </CardContent>
            </Card>
          )}

          {/* AI Score Card */}
          <Card className="border-t-4 border-[#4B2D6E]" data-testid="ai-score-card">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-[#4B2D6E]">AI Score</CardTitle>
            </CardHeader>
            <CardContent>
              <div className={`text-center p-6 rounded-xl border-2 ${getScoreBg(candidate.ai_score)}`}>
                <p className={`text-5xl font-bold ${getScoreColor(candidate.ai_score)}`}>
                  {candidate.ai_score || '—'}
                </p>
                <p className="text-sm text-gray-600 mt-1">out of 100</p>
              </div>
              
              {/* Score Breakdown */}
              {candidate.ai_breakdown && (
                <div className="mt-4 space-y-2">
                  {Object.entries(candidate.ai_breakdown).map(([key, value]) => (
                    <div key={key} className="flex items-center justify-between text-sm">
                      <span className="text-[#666666] capitalize">{key.replace('_', ' ')}</span>
                      <span className={`font-medium ${getScoreColor(value)}`}>{value}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Flags */}
              {candidate.ai_flags?.length > 0 && (
                <div className="mt-4 p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center gap-2 text-red-700 font-medium mb-2">
                    <AlertTriangle className="h-4 w-4" />
                    Red Flags
                  </div>
                  <ul className="text-sm text-red-600 space-y-1">
                    {candidate.ai_flags.map((flag, i) => (
                      <li key={i}>• {flag}</li>
                    ))}
                  </ul>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Contact Info */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-[#4B2D6E]">Contact Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {(parsed.email || candidate.contact_info?.email) && (
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-[#666666]" />
                  <span>{candidate.contact_info?.email || parsed.email}</span>
                </div>
              )}
              {candidate.contact_info?.home_phone && (
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-[#666666]" />
                  <span>{candidate.contact_info.home_phone} (Home)</span>
                </div>
              )}
              {candidate.contact_info?.mobile_phone && (
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-[#666666]" />
                  <span>{candidate.contact_info.mobile_phone} (Mobile)</span>
                </div>
              )}
              {!candidate.contact_info?.home_phone && !candidate.contact_info?.mobile_phone && (parsed.phone || answers.phone_number) && (
                <div className="flex items-center gap-3">
                  <Phone className="h-4 w-4 text-[#666666]" />
                  <span>{answers.phone_number || parsed.phone}</span>
                </div>
              )}
              {candidate.contact_info?.preferred_communication?.length > 0 && (
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-[#666666]" />
                  <span className="text-sm text-[#666]">
                    Prefers: {candidate.contact_info.preferred_communication.map(m => 
                      m === 'phone' ? 'Phone Call' : m === 'text' ? 'Text' : 'Email'
                    ).join(', ')}
                  </span>
                </div>
              )}
              {!candidate.contact_info && !parsed.email && !parsed.phone && !answers.phone_number && (
                <p className="text-sm text-[#999]">No contact information provided</p>
              )}
            </CardContent>
          </Card>

          {/* Resume */}
          {candidate.resume && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-[#4B2D6E]">Resume</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-3 p-3 bg-[#F5F5F5] rounded-lg">
                  <FileText className="h-8 w-8 text-[#4B2D6E]" />
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{candidate.resume.filename}</p>
                    <p className="text-sm text-[#666666]">
                      Uploaded {new Date(candidate.resume.uploaded_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-[#4B2D6E] text-[#4B2D6E]"
                      onClick={async () => {
                        try {
                          const response = await axios.get(`${API}/candidates/${candidateId}/resume/download`, { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          window.open(url, '_blank');
                        } catch {
                          toast.error('Failed to open resume');
                        }
                      }}
                      data-testid="view-resume-btn"
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="border-[#4B2D6E] text-[#4B2D6E]"
                      onClick={async () => {
                        try {
                          const response = await axios.get(`${API}/candidates/${candidateId}/resume/download`, { responseType: 'blob' });
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', candidate.resume.filename);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                          window.URL.revokeObjectURL(url);
                        } catch {
                          toast.error('Failed to download resume');
                        }
                      }}
                      data-testid="download-resume-btn"
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Offer Letter Status */}
          {offerLetter && offerLetter.status !== 'not_sent' && (
            <Card className="border-t-4 border-[#4CAF50]">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-[#4B2D6E] flex items-center gap-2">
                  <FileSignature className="h-5 w-5" />
                  Offer Letter
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#666666]">Status</span>
                  <Badge className={offerLetter.status === 'signed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}>
                    {offerLetter.status === 'signed' ? 'Signed' : 'Awaiting Signature'}
                  </Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-[#666666]">Sent</span>
                  <span className="text-sm">{new Date(offerLetter.sent_at).toLocaleDateString()}</span>
                </div>
                {offerLetter.signed_at && (
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-[#666666]">Signed</span>
                    <span className="text-sm">{new Date(offerLetter.signed_at).toLocaleDateString()}</span>
                  </div>
                )}
                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full">
                      <Eye className="mr-2 h-4 w-4" /> View Letter
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Offer Letter</DialogTitle>
                    </DialogHeader>
                    <div className="whitespace-pre-wrap font-serif p-4 border rounded-lg bg-white">
                      {offerLetter.letter_content}
                    </div>
                    {offerLetter.signature && (
                      <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="font-medium text-green-800">Signed by: {offerLetter.signature.name}</p>
                        <p className="text-sm text-green-600">Date: {new Date(offerLetter.signature.signed_at).toLocaleString()}</p>
                      </div>
                    )}
                  </DialogContent>
                </Dialog>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Right Column - Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* AI Summary */}
          {candidate.ai_summary && (
            <Card className="border-l-4 border-[#E8722A]" data-testid="ai-summary-card">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg text-[#4B2D6E]">AI Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-[#333333] leading-relaxed">{candidate.ai_summary}</p>
              </CardContent>
            </Card>
          )}

          {/* Tabs */}
          <Card>
            <Tabs defaultValue="answers" className="w-full">
              <CardHeader className="pb-0">
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="answers">Answers</TabsTrigger>
                  <TabsTrigger value="notes">Notes</TabsTrigger>
                  <TabsTrigger value="interview">Interview</TabsTrigger>
                  {candidate.status === 'hired' && (
                    <TabsTrigger value="onboarding">Onboarding</TabsTrigger>
                  )}
                </TabsList>
              </CardHeader>

              {/* Screening Answers Tab */}
              <TabsContent value="answers" className="p-6 space-y-6">
                {/* Quick Facts */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="p-3 bg-[#F5F5F5] rounded-lg">
                    <p className="text-sm text-[#666666]">Experience</p>
                    <p className="font-semibold text-[#4B2D6E]">{answers.experience_years || '—'} years</p>
                  </div>
                  <div className="p-3 bg-[#F5F5F5] rounded-lg">
                    <p className="text-sm text-[#666666]">Employment</p>
                    <p className="font-semibold text-[#4B2D6E]">{answers.employment_type || '—'}</p>
                  </div>
                  <div className="p-3 bg-[#F5F5F5] rounded-lg">
                    <p className="text-sm text-[#666666]">Start Date</p>
                    <p className="font-semibold text-[#4B2D6E]">{answers.start_date || '—'}</p>
                  </div>
                  <div className="p-3 bg-[#F5F5F5] rounded-lg">
                    <p className="text-sm text-[#666666]">Driver's License</p>
                    <p className="font-semibold text-[#4B2D6E]">{answers.drivers_license || '—'}</p>
                  </div>
                </div>

                {/* All Answers */}
                <div className="space-y-4">
                  {Object.entries(answers).map(([key, value]) => (
                    <div key={key} className="border-b border-gray-100 pb-3">
                      <p className="text-sm text-[#666666] capitalize mb-1">
                        {key.replace(/_/g, ' ')}
                      </p>
                      <p className="text-[#333333]">
                        {Array.isArray(value) ? value.join(', ') : value || '—'}
                      </p>
                    </div>
                  ))}
                </div>
              </TabsContent>

              {/* Notes Tab */}
              <TabsContent value="notes" className="p-6 space-y-4">
                {/* Add Note Form */}
                <div className="space-y-3">
                  <Textarea
                    placeholder="Add a note about this candidate..."
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    className="min-h-24"
                    data-testid="note-input"
                  />
                  <div className="flex items-center justify-between">
                    <Select value={noteType} onValueChange={setNoteType}>
                      <SelectTrigger className="w-40">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="private">Private Note</SelectItem>
                        <SelectItem value="interview">Interview Note</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      onClick={addNote}
                      disabled={submitting || !newNote.trim()}
                      className="bg-[#E8722A] hover:bg-[#D6621F]"
                      data-testid="add-note-btn"
                    >
                      <Send className="mr-2 h-4 w-4" /> Add Note
                    </Button>
                  </div>
                </div>

                {/* Existing Notes */}
                <div className="space-y-3 mt-6">
                  {candidate.notes?.length === 0 ? (
                    <p className="text-center text-[#666666] py-8">No notes yet</p>
                  ) : (
                    candidate.notes?.map((note) => (
                      <div key={note.id} className="p-4 bg-[#F5F5F5] rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <Badge variant="outline">{note.type}</Badge>
                          <span className="text-sm text-[#666666]">
                            {note.created_by} • {new Date(note.created_at).toLocaleString()}
                          </span>
                        </div>
                        <p className="text-[#333333]">{note.content}</p>
                      </div>
                    ))
                  )}
                </div>
              </TabsContent>

              {/* Interview Tab — Multi-Phase Pipeline */}
              <TabsContent value="interview" className="p-6 space-y-6">
                {/* Phase 2: Phone Interview */}
                <div className="border rounded-lg p-5" data-testid="phone-interview-section">
                  <h3 className="font-semibold text-[#4B2D6E] flex items-center gap-2 mb-3">
                    <Phone className="h-5 w-5" />
                    Phase 2 — Phone Interview
                  </h3>
                  {candidate.phone_interview ? (
                    <div className="flex items-start gap-4 bg-violet-50 rounded-lg p-4">
                      <Calendar className="h-8 w-8 text-violet-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-violet-800">
                          {['phone_scheduled', 'phone_completed', 'in_person_invited', 'in_person_scheduled', 'interviewed', 'hired'].includes(candidate.status)
                            ? 'Phone Interview Scheduled'
                            : 'Scheduling Link Sent'}
                        </p>
                        <p className="text-sm text-[#666] mt-1">
                          <strong>Date:</strong> {candidate.phone_interview.date} &nbsp;
                          <strong>Time:</strong> {candidate.phone_interview.time} &nbsp;
                          <strong>Duration:</strong> {candidate.phone_interview.duration_minutes} min
                        </p>
                        {candidate.phone_interview.admin_name && (
                          <p className="text-xs text-[#999] mt-1">With: {candidate.phone_interview.admin_name}</p>
                        )}
                        {['phone_completed', 'in_person_invited', 'in_person_scheduled', 'interviewed', 'hired'].includes(candidate.status) && (
                          <Badge className="bg-green-100 text-green-800 mt-2">Passed</Badge>
                        )}
                      </div>
                    </div>
                  ) : candidate.status === 'phone_screen' ? (
                    <div className="bg-yellow-50 rounded-lg p-4 text-center">
                      <Clock className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                      <p className="font-medium text-yellow-800">Waiting for candidate to schedule</p>
                      <p className="text-sm text-[#666] mt-1">Scheduling link has been sent via SMS and email.</p>
                    </div>
                  ) : ['pending_review', 'approved'].includes(candidate.status) ? (
                    <p className="text-sm text-[#999]">Candidate has not been advanced to phone interview yet.</p>
                  ) : (
                    <p className="text-sm text-[#999]">No phone interview data.</p>
                  )}
                </div>

                {/* Phase 3: In-Person Interview */}
                <div className="border rounded-lg p-5" data-testid="in-person-interview-section">
                  <h3 className="font-semibold text-[#4B2D6E] flex items-center gap-2 mb-3">
                    <Calendar className="h-5 w-5" />
                    Phase 3 — In-Person Interview
                  </h3>
                  {candidate.in_person_interview ? (
                    <div className="flex items-start gap-4 bg-sky-50 rounded-lg p-4">
                      <Calendar className="h-8 w-8 text-sky-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-sky-800">
                          {['in_person_scheduled', 'interviewed', 'hired'].includes(candidate.status)
                            ? 'In-Person Interview Scheduled'
                            : 'Scheduling Link Sent'}
                        </p>
                        <p className="text-sm text-[#666] mt-1">
                          <strong>Date:</strong> {candidate.in_person_interview.date} &nbsp;
                          <strong>Time:</strong> {candidate.in_person_interview.time} &nbsp;
                          <strong>Duration:</strong> {candidate.in_person_interview.duration_minutes} min
                        </p>
                        {candidate.in_person_interview.admin_name && (
                          <p className="text-xs text-[#999] mt-1">With: {candidate.in_person_interview.admin_name}</p>
                        )}
                        {['interviewed', 'hired'].includes(candidate.status) && (
                          <Badge className="bg-green-100 text-green-800 mt-2">Completed</Badge>
                        )}
                      </div>
                    </div>
                  ) : candidate.status === 'in_person_invited' ? (
                    <div className="bg-yellow-50 rounded-lg p-4 text-center">
                      <Clock className="h-8 w-8 text-yellow-600 mx-auto mb-2" />
                      <p className="font-medium text-yellow-800">Waiting for candidate to schedule</p>
                      <p className="text-sm text-[#666] mt-1">Scheduling link has been sent via SMS and email.</p>
                    </div>
                  ) : ['pending_review', 'approved', 'phone_screen', 'phone_scheduled'].includes(candidate.status) ? (
                    <p className="text-sm text-[#999]">Phone interview must be completed first.</p>
                  ) : candidate.status === 'phone_completed' ? (
                    <p className="text-sm text-[#999]">Ready to advance to in-person interview.</p>
                  ) : (
                    <p className="text-sm text-[#999]">No in-person interview data.</p>
                  )}
                </div>

                {/* Legacy interview display */}
                {candidate.interview_date && !candidate.phone_interview && !candidate.in_person_interview && (
                  <div className="text-center py-6 border rounded-lg">
                    <Calendar className="h-10 w-10 text-[#4CAF50] mx-auto mb-3" />
                    <h3 className="text-lg font-bold text-[#4B2D6E]">Interview Scheduled (Legacy)</h3>
                    <p className="text-[#666666] mt-1">
                      {candidate.interview_date} at {candidate.interview_time}
                    </p>
                  </div>
                )}
              </TabsContent>

              {/* Onboarding Tab */}
              {candidate.status === 'hired' && (
                <TabsContent value="onboarding" className="p-6">
                  <div className="space-y-4">
                    {candidate.onboarding && Object.entries(candidate.onboarding).map(([key, value]) => (
                      <div key={key} className="flex items-center space-x-3">
                        <Checkbox
                          id={key}
                          checked={value}
                          onCheckedChange={(checked) => updateOnboarding(key, checked)}
                          className="border-[#4B2D6E] data-[state=checked]:bg-[#4CAF50]"
                        />
                        <Label htmlFor={key} className="text-[#333333] capitalize cursor-pointer">
                          {key.replace(/_/g, ' ')}
                        </Label>
                      </div>
                    ))}
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </Card>

          {/* Background Check Card */}
          {isOwner && ['hired', 'interviewed', 'interview_confirmed'].includes(candidate.status) && (
            <Card data-testid="background-check-card">
              <CardHeader>
                <CardTitle className="text-lg font-semibold text-[#4B2D6E] flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Background Check
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {(!bgCheck || bgCheck.status === 'not_initiated') ? (
                  <div className="text-center py-4">
                    <Shield className="h-10 w-10 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm text-[#666666] mb-3">No background check has been initiated</p>
                    <Button
                      onClick={initiateBackgroundCheck}
                      disabled={bgCheckLoading}
                      className="bg-[#4B2D6E] hover:bg-[#3a2357]"
                      data-testid="initiate-bg-check-btn"
                    >
                      {bgCheckLoading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Shield className="mr-2 h-4 w-4" />
                      )}
                      Initiate Background Check
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-[#333]">Status</span>
                      <Badge className={
                        bgCheck.status === 'complete' && bgCheck.result === 'clear'
                          ? 'bg-green-100 text-green-800'
                          : bgCheck.status === 'complete' && bgCheck.result === 'consider'
                          ? 'bg-yellow-100 text-yellow-800'
                          : bgCheck.status === 'error'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-blue-100 text-blue-800'
                      } data-testid="bg-check-status">
                        {bgCheck.status === 'complete' ? bgCheck.result?.toUpperCase() : bgCheck.status?.replace(/_/g, ' ').toUpperCase()}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[#666]">Package</span>
                      <span className="text-sm font-medium">{bgCheck.package?.replace(/_/g, ' ')}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-[#666]">Initiated</span>
                      <span className="text-sm">{new Date(bgCheck.initiated_at).toLocaleDateString()}</span>
                    </div>
                    {bgCheck.completed_at && (
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-[#666]">Completed</span>
                        <span className="text-sm">{new Date(bgCheck.completed_at).toLocaleDateString()}</span>
                      </div>
                    )}
                    {bgCheck.is_mocked && (
                      <div className="border-t pt-3">
                        <p className="text-xs text-amber-600 mb-2">Checkr API not configured — manual status update:</p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white text-xs"
                            onClick={() => updateBgCheckStatus(bgCheck.id, 'clear')}
                            data-testid="bg-mark-clear"
                          >
                            Mark Clear
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-yellow-500 text-yellow-700 text-xs"
                            onClick={() => updateBgCheckStatus(bgCheck.id, 'consider')}
                            data-testid="bg-mark-consider"
                          >
                            Mark Consider
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Activity Audit Trail (Part 5) */}
      <Card data-testid="candidate-activity-log">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock className="h-5 w-5 text-[#4B2D6E]" />
            Activity Trail
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingActivity ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="h-6 w-6 animate-spin text-[#4B2D6E]" />
            </div>
          ) : activityLog.length === 0 ? (
            <p className="text-sm text-[#999] text-center py-6">No activity recorded for this candidate yet.</p>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              {activityLog.map((entry, index) => (
                <div key={entry.id || index} className="flex items-start gap-3 relative" data-testid={`candidate-activity-${entry.id}`}>
                  {index < activityLog.length - 1 && (
                    <div className="absolute left-[15px] top-8 bottom-0 w-px bg-gray-200" />
                  )}
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0 z-10 ${
                    entry.actor_role === 'owner' ? 'bg-[#4B2D6E]' : 
                    entry.actor_role === 'va' ? 'bg-blue-500' : 
                    entry.actor_role === 'system' ? 'bg-gray-400' : 'bg-amber-500'
                  }`}>
                    {entry.actor_name?.charAt(0)?.toUpperCase() || 'S'}
                  </div>
                  <div className="flex-1 pb-3">
                    <p className="text-sm">
                      <span className="font-medium text-[#333]">{entry.actor_name}</span>{' '}
                      <span className="text-[#666]">{entry.action}</span>
                    </p>
                    <p className="text-xs text-[#999] mt-0.5">
                      {new Date(entry.timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Approval Confirmation Dialog */}
      <AlertDialog open={approveDialogOpen} onOpenChange={setApproveDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Candidate Approval</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to approve <strong>{candidate?.parsed_resume?.name || 'this candidate'}</strong>. 
              This will notify them via SMS/email that they've moved forward in the hiring process. 
              You can undo this action later if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-approve-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-green-600 hover:bg-green-700"
              onClick={() => updateStatus('approved')}
              data-testid="confirm-approve-btn"
            >
              Yes, Approve
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CandidateDetailPage;
